import React, { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Link, useNavigate } from 'react-router-dom'
import { FaArrowLeft } from 'react-icons/fa'
import Breadcrum from '../../components/breadcrum'
import { createGraph, getAllGraph, prefillGraphs } from '../../api/graphApi'

// Graphs for several companies at once (an article's company and its competitors), filled
// from their uploaded Excel. Nothing is saved until "Create" is pressed.
const BulkAddGraph = () => {
  useEffect(() => {
    document.title = 'Add Graphs from DSJ data'
  }, [])

  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [text, setText] = useState('')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const { data: graphs = [] } = useQuery({
    queryKey: ['graphs'],
    queryFn: getAllGraph,
  })
  const existingCins = new Set(
    graphs.map((g) => (g.cin || '').toUpperCase()).filter(Boolean)
  )

  const fetchAll = async () => {
    const queries = text
      .split(/[\n,]/)
      .map((q) => q.trim())
      .filter(Boolean)
    if (queries.length === 0) {
      toast.error('Enter at least one company name, brand or CIN')
      return
    }
    if (queries.length > 20) {
      toast.error('At most 20 companies at a time')
      return
    }
    setLoading(true)
    try {
      const results = await prefillGraphs(queries)
      setRows(
        results.map((r) => {
          const exists =
            r.status === 'found' &&
            existingCins.has((r.company.cin || '').toUpperCase())
          return {
            ...r,
            brandName: r.company?.brandName || '',
            exists,
            selected: r.status === 'found' && !exists,
            result: null,
          }
        })
      )
    } catch (error) {
      toast.error(error.response?.data || 'Could not load DSJ data')
    } finally {
      setLoading(false)
    }
  }

  const updateRow = (index, patch) =>
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)))

  const pickCandidate = async (index, candidate) => {
    try {
      const [r] = await prefillGraphs([candidate.cin || candidate.companyName])
      if (r?.status !== 'found') {
        toast.error('Could not load that company')
        return
      }
      const exists = existingCins.has((r.company.cin || '').toUpperCase())
      updateRow(index, {
        ...r,
        brandName: r.company.brandName || '',
        exists,
        selected: !exists,
      })
    } catch (error) {
      toast.error(error.response?.data || 'Could not load DSJ data')
    }
  }

  const createSelected = async () => {
    const chosen = rows
      .map((r, i) => ({ r, i }))
      .filter(({ r }) => r.selected && r.status === 'found' && !r.result?.ok)
    if (chosen.length === 0) {
      toast.error('Select at least one company')
      return
    }
    const missingBrand = chosen.find(({ r }) => !r.brandName.trim())
    if (missingBrand) {
      toast.error(`Enter a brand name for ${missingBrand.r.company.companyName}`)
      return
    }
    // The company profile box shows the logo, and the create route needs it with that box.
    const missingLogo = chosen.find(({ r }) => !r.logo)
    if (missingLogo) {
      toast.error(`Choose a logo for ${missingLogo.r.company.companyName}`)
      return
    }
    setSaving(true)
    let created = 0
    // One at a time, so each row shows its own result.
    for (const { r, i } of chosen) {
      try {
        await createGraph({
          ...r.company,
          brandName: r.brandName.trim(),
          image: [r.logo],
          hideInfoBox: false,
          hideGraph: false,
          hideFundRaise: !r.company.fundRaiseYear,
        })
        created += 1
        updateRow(i, { result: { ok: true, message: 'Created' } })
      } catch (error) {
        // The create route answers a failure with an empty body.
        const body = error.response?.data
        updateRow(i, {
          result: {
            ok: false,
            message: (
              (typeof body === 'string' && body) ||
              body?.message ||
              'Could not create (a graph with this CIN may already exist)'
            ).slice(0, 200),
          },
        })
      }
    }
    setSaving(false)
    queryClient.invalidateQueries({ queryKey: ['graphs'] })
    if (created) toast.success(`${created} graph(s) created`)
  }

  return (
    <>
      <Breadcrum heading="Deals" subheading="Add Graphs from DSJ data" />

      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex justify-center items-center bg-gray-500 p-2 text-white rounded"
      >
        <FaArrowLeft />
        &nbsp; Back
      </button>

      <div className="max-w-8xl text-left mx-auto px-4 py-6">
        <div className="bg-white p-6 rounded-xl shadow mb-6">
          <h2 className="font-semibold text-gray-800 mb-1">Companies</h2>
          <p className="text-sm text-gray-600 mb-3">
            One per line (or comma separated): company name, brand or CIN. Use
            this for an article's company and all its competitors in one go.
          </p>
          <textarea
            rows={5}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={'Asaya\nSkinInspired\nU24246RJ2022PTC080216'}
            className="border border-gray-300 rounded-lg p-3 w-full focus:outline-none focus:border-orange-500"
          />
          <button
            type="button"
            onClick={fetchAll}
            disabled={loading}
            className="mt-3 bg-[#ff7010] text-white px-4 py-2 rounded-lg disabled:opacity-60"
          >
            {loading ? 'Loading…' : 'Get data'}
          </button>
        </div>

        {rows.length > 0 && (
          <div className="bg-white p-6 rounded-xl shadow">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-600 border-b">
                    <th className="p-2"></th>
                    <th className="p-2">You typed</th>
                    <th className="p-2">Company</th>
                    <th className="p-2">Brand name *</th>
                    <th className="p-2">Logo *</th>
                    <th className="p-2">Revenue / EBITDA / Net</th>
                    <th className="p-2">Years</th>
                    <th className="p-2">Fund raise</th>
                    <th className="p-2">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={`${r.query}-${i}`} className="border-b align-top">
                      <td className="p-2">
                        {r.status === 'found' && (
                          <input
                            type="checkbox"
                            checked={r.selected}
                            disabled={!!r.result?.ok}
                            onChange={() => updateRow(i, { selected: !r.selected })}
                          />
                        )}
                      </td>
                      <td className="p-2">{r.query}</td>
                      <td className="p-2">
                        {r.status === 'found' && (
                          <>
                            <div className="font-semibold">{r.company.companyName}</div>
                            <div className="text-gray-500">{r.company.cin}</div>
                            {r.exists && (
                              <div className="text-amber-700">
                                A graph with this CIN already exists
                              </div>
                            )}
                            {(!r.company.industry || !r.company.yearIncorporation) && (
                              <div className="text-amber-700">
                                Not in the Excel:{' '}
                                {[
                                  !r.company.industry && 'industry',
                                  !r.company.yearIncorporation && 'year of incorporation',
                                ]
                                  .filter(Boolean)
                                  .join(', ')}{' '}
                                (add with Update later)
                              </div>
                            )}
                          </>
                        )}
                        {r.status === 'not_found' && (
                          <span className="text-red-600">Not in DSJ data</span>
                        )}
                        {r.status === 'ambiguous' && (
                          <div>
                            <div className="text-gray-700 mb-1">
                              More than one match, pick one:
                            </div>
                            {(r.candidates || []).map((c) => (
                              <button
                                type="button"
                                key={c.cin || c.companyName}
                                onClick={() => pickCandidate(i, c)}
                                className="block text-left text-[#ff7010] underline mb-1"
                              >
                                {c.brandName ? `${c.brandName} — ` : ''}
                                {c.companyName}
                              </button>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="p-2">
                        {r.status === 'found' && (
                          <input
                            type="text"
                            value={r.brandName}
                            onChange={(e) => updateRow(i, { brandName: e.target.value })}
                            className="border border-gray-300 rounded p-1 w-36"
                          />
                        )}
                      </td>
                      <td className="p-2">
                        {r.status === 'found' && (
                          <input
                            type="file"
                            accept="image/*"
                            disabled={!!r.result?.ok}
                            onChange={(e) => updateRow(i, { logo: e.target.files?.[0] || null })}
                            className="w-44 text-xs"
                          />
                        )}
                      </td>
                      <td className="p-2">
                        {r.status === 'found' && (
                          <>
                            <div>{r.company.grossRevenue}</div>
                            <div>{r.company.ebitda}</div>
                            <div>{r.company.netProfitLoss}</div>
                          </>
                        )}
                      </td>
                      <td className="p-2">{r.company?.grossYear}</td>
                      <td className="p-2">
                        {r.status === 'found' &&
                          (r.company.fundRaiseYear ? (
                            <>
                              <div>{r.company.fundRaiseRound}</div>
                              <div className="text-gray-500">{r.company.fundRaiseYear}</div>
                            </>
                          ) : (
                            <span className="text-gray-400">None in Excel</span>
                          ))}
                      </td>
                      <td className="p-2">
                        {r.result && (
                          <span className={r.result.ok ? 'text-green-700' : 'text-red-600'}>
                            {r.result.message}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-sm text-gray-600 mt-4">
              Logos are not in the Excel, so choose one for each company. Every
              other field can still be changed later with Update on the Graph
              page.
            </p>
            <div className="flex gap-3 mt-4">
              <button
                type="button"
                onClick={createSelected}
                disabled={saving}
                className="bg-[#ff7010] text-white px-4 py-2 rounded-lg disabled:opacity-60"
              >
                {saving ? 'Creating…' : 'Create selected graphs'}
              </button>
              <Link
                to="/dealdashboard/graph"
                className="px-4 py-2 rounded-lg border border-gray-300"
              >
                Go to Graph list
              </Link>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default BulkAddGraph
